import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Sparkles, Image, ShoppingBag, Brain, Play, ArrowRight, Star, Shield, Palette, Trophy, Diamond, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import FeatureDemoModal from "@/components/FeatureDemoModal";
import HeroCarousel from "@/components/HeroCarousel";
import FeaturedArtists from "@/components/FeaturedArtists";

const Index = () => {
  const navigate = useNavigate();
  const [demoFeature, setDemoFeature] = useState<'gallery' | 'analysis' | 'marketplace' | 'collection' | null>(null);
  
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Art Experto | Luxury Art Discovery Platform"
        description="Discover, analyze, and collect exceptional artwork with Art Experto. AI-powered art analysis, curated galleries, secure marketplace, and live auctions for discerning collectors."
        url="https://art-insight-oracle.lovable.app"
      />
      <Navbar />
      
      <main>
        {/* Hero Section with Featured Artworks Carousel */}
        <section className="relative pt-28 pb-20 px-4 overflow-hidden">
          {/* Elegant background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-gradient-radial opacity-60 animate-float" />
            <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-gradient-radial opacity-40 animate-float animation-delay-300" />
            {/* Decorative lines */}
            <div className="absolute top-32 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
            <div className="absolute bottom-32 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
          </div>

          <div className="container mx-auto relative z-10">
            {/* Header content */}
            <div className="text-center mb-14">
              <div className="opacity-0 animate-fade-in-up">
                <div className="inline-flex items-center gap-3 mb-8 px-6 py-3 glass rounded-full luxury-border">
                  <Diamond className="h-4 w-4 text-primary" />
                  <span className="text-elegant text-xs text-primary tracking-luxury">Curated Excellence Since 2024</span>
                  <Diamond className="h-4 w-4 text-primary" />
                </div>
              </div>
              
              <h1 className="opacity-0 animate-fade-in-up animation-delay-200 mb-8">
                <span className="block text-5xl md:text-7xl lg:text-display-lg font-display font-medium text-foreground mb-2">
                  Art Insight
                </span>
                <span className="block text-6xl md:text-8xl lg:text-display-xl font-display font-semibold luxury-text">
                  Oracle
                </span>
              </h1>
              
              <div className="opacity-0 animate-fade-in-up animation-delay-400 max-w-2xl mx-auto mb-10">
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-light">
                  Where <span className="text-foreground font-medium">discerning collectors</span> discover exceptional art.
                </p>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-light mt-2">
                  AI-powered insights meet <span className="luxury-text font-medium">timeless elegance</span>.
                </p>
              </div>
              
              {/* Decorative element */}
              <div className="opacity-0 animate-fade-in-up animation-delay-500 flex justify-center mb-10">
                <div className="w-24 h-px bg-gradient-gold" />
              </div>
            </div>

            {/* Featured Artworks Carousel */}
            <div className="opacity-0 animate-fade-in-up animation-delay-600 mb-14">
              <HeroCarousel />
            </div>

            {/* CTA Buttons */}
            <div className="opacity-0 animate-fade-in-up animation-delay-700 flex flex-col sm:flex-row gap-5 justify-center">
              <Button 
                size="lg" 
                onClick={() => navigate("/gallery")} 
                className="bg-gradient-hero text-primary-foreground shadow-gold hover:shadow-glow transition-all duration-700 text-base px-12 py-7 group font-medium tracking-wide"
              >
                <Sparkles className="mr-3 h-5 w-5" />
                Explore Collection
                <ArrowRight className="ml-3 h-5 w-5 transition-transform duration-500 group-hover:translate-x-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => navigate("/art-analysis")} 
                className="text-base px-12 py-7 luxury-border hover:bg-primary/5 transition-all duration-500 font-medium tracking-wide"
              >
                <Brain className="mr-3 h-5 w-5" />
                AI Analysis
              </Button>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-20 px-4 border-y border-border/30 bg-muted/20">
          <div className="container mx-auto">
            <div className="flex flex-wrap justify-center items-center gap-16 md:gap-24">
              {[
                { icon: Shield, label: "Verified Artists" },
                { icon: Palette, label: "Curated Selection" },
                { icon: Trophy, label: "Premium Quality" },
                { icon: Crown, label: "Expert Analysis" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 group cursor-default">
                  <div className="p-3 rounded-full bg-primary/5 group-hover:bg-primary/10 transition-colors duration-500">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-elegant text-xs text-muted-foreground tracking-elegant group-hover:text-foreground transition-colors duration-500">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Artists Section */}
        <FeaturedArtists />

        {/* Features Section */}
        <section className="py-28 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-20">
              <span className="inline-block text-elegant text-xs text-primary tracking-luxury mb-6">
                Our Services
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium text-foreground luxury-divider pb-8">
                Elevate Your Art Journey
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Gallery Card */}
              <Card 
                className="group relative p-10 hover:shadow-strong transition-all duration-700 bg-card border-border/30 overflow-hidden cursor-pointer hover-lift ornate-corners"
                onClick={() => navigate("/gallery")}
              >
                <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-20 h-20 bg-gradient-hero rounded-2xl flex items-center justify-center shadow-gold group-hover:shadow-glow group-hover:scale-105 transition-all duration-500">
                      <Image className="h-10 w-10 text-primary-foreground" />
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setDemoFeature('gallery'); }}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-500 text-primary"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Demo
                    </Button>
                  </div>
                  <h3 className="text-3xl font-display font-medium mb-4 group-hover:text-primary transition-colors duration-500">
                    Curated Gallery
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-light">
                    Explore meticulously curated collections from world-renowned and emerging artists. 
                    Every piece tells a story worth discovering.
                  </p>
                </div>
              </Card>

              {/* AI Analysis Card */}
              <Card 
                className="group relative p-10 hover:shadow-strong transition-all duration-700 bg-card border-border/30 overflow-hidden cursor-pointer hover-lift ornate-corners"
                onClick={() => navigate("/art-analysis")}
              >
                <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-20 h-20 bg-gradient-accent rounded-2xl flex items-center justify-center group-hover:shadow-glow group-hover:scale-105 transition-all duration-500">
                      <Brain className="h-10 w-10 text-accent-foreground" />
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setDemoFeature('analysis'); }}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-500 text-accent"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Demo
                    </Button>
                  </div>
                  <h3 className="text-3xl font-display font-medium mb-4 group-hover:text-accent transition-colors duration-500">
                    AI-Powered Insights
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-light">
                    Unlock deep understanding with intelligent analysis of style, technique, 
                    provenance, and market context powered by advanced AI.
                  </p>
                </div>
              </Card>

              {/* Marketplace Card */}
              <Card 
                className="group relative p-10 hover:shadow-strong transition-all duration-700 bg-card border-border/30 overflow-hidden cursor-pointer hover-lift ornate-corners"
                onClick={() => navigate("/marketplace")}
              >
                <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-20 h-20 bg-gradient-luxury rounded-2xl flex items-center justify-center group-hover:shadow-glow group-hover:scale-105 transition-all duration-500">
                      <ShoppingBag className="h-10 w-10 text-secondary-foreground" />
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setDemoFeature('marketplace'); }}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-500"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Demo
                    </Button>
                  </div>
                  <h3 className="text-3xl font-display font-medium mb-4 group-hover:text-primary transition-colors duration-500">
                    Exclusive Marketplace
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-light">
                    Acquire unique masterpieces through our secure, verified marketplace. 
                    Every transaction backed by our authenticity guarantee.
                  </p>
                </div>
              </Card>

              {/* Collection Card */}
              <Card 
                className="group relative p-10 hover:shadow-strong transition-all duration-700 bg-card border-border/30 overflow-hidden cursor-pointer hover-lift ornate-corners"
                onClick={() => navigate("/my-artworks")}
              >
                <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-20 h-20 bg-gradient-gold rounded-2xl flex items-center justify-center shadow-gold group-hover:shadow-glow group-hover:scale-105 transition-all duration-500">
                      <Sparkles className="h-10 w-10 text-primary-foreground" />
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={(e) => { e.stopPropagation(); setDemoFeature('collection'); }}
                      className="opacity-0 group-hover:opacity-100 transition-all duration-500 text-primary"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Demo
                    </Button>
                  </div>
                  <h3 className="text-3xl font-display font-medium mb-4 group-hover:text-primary transition-colors duration-500">
                    Personal Collection
                  </h3>
                  <p className="text-muted-foreground leading-relaxed font-light">
                    Manage your art portfolio with sophisticated tools. Track value, 
                    organize exhibitions, and share your passion with the world.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-28 px-4">
          <div className="container mx-auto">
            <Card className="relative overflow-hidden p-16 md:p-20 bg-gradient-noir border-none shadow-strong">
              {/* Decorative elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
                <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
              </div>
              
              <div className="relative z-10 text-center max-w-3xl mx-auto">
                <Diamond className="h-8 w-8 text-primary mx-auto mb-8 animate-pulse-gold" />
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium mb-8 text-secondary-foreground">
                  Begin Your Collection Today
                </h2>
                <p className="text-xl mb-12 text-secondary-foreground/60 leading-relaxed font-light">
                  Join an exclusive community of collectors and art enthusiasts. 
                  Your next masterpiece awaits.
                </p>
                <div className="flex flex-col sm:flex-row gap-5 justify-center">
                  <Button 
                    size="lg" 
                    onClick={() => navigate("/auth")} 
                    className="bg-gradient-hero text-primary-foreground hover:opacity-90 text-base px-12 py-7 shadow-gold hover:shadow-glow transition-all duration-500 font-medium tracking-wide"
                  >
                    Create Free Account
                    <ArrowRight className="ml-3 h-5 w-5" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="secondary" 
                    onClick={() => navigate("/gallery")}
                    className="text-base px-12 py-7 transition-all duration-500 font-medium tracking-wide"
                  >
                    Browse Gallery
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
      
      <FeatureDemoModal open={demoFeature !== null} onOpenChange={open => !open && setDemoFeature(null)} feature={demoFeature} />
    </div>
  );
};

export default Index;